import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image } from "../../../../components/common/image";
import { Text } from "../../../../components/common/typography";
import { SectionCard } from "../../../../components/common/section-card";
import type { OrderStatus } from "../../../../components/common/chip";
import type { PurchaseOrderLineDto } from "../../../../api";
import { api } from "../../../api_client";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { resolveProductRouteFromVariant } from "../../../products/utils/resolve-product-route";
import { showErrorToast } from "../../../../utils/toasts";

function AuthenticatedImage({ src, alt }: { src: string; alt: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    setBlobUrl(null);

    api
      .get(src, { responseType: "blob" })
      .then((response) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (!active) return;
        setBlobUrl(null);
      });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  return <Image image={blobUrl} alt={alt} size="w-14 h-14 object-cover rounded-lg" />;
}

interface Props {
  items: PurchaseOrderLineDto[];
  status: OrderStatus;
}

export function SellerOrderItemsCard({ items, status }: Props) {
  const isDelivered = status === "delivered" || status === "validated";
  const navigate = useNavigate();
  const [navigatingId, setNavigatingId] = useState<number | null>(null);

  async function handleRowClick(variantId: number) {
    if (navigatingId != null) return;
    setNavigatingId(variantId);
    try {
      const href = await resolveProductRouteFromVariant(variantId, AppRoutes.MY_PRODUCT_DETAILS);
      navigate(href);
    } catch {
      showErrorToast("Could not open product details.");
    } finally {
      setNavigatingId(null);
    }
  }

  return (
    <SectionCard
      shadow
      title={isDelivered ? "Items Delivered" : "Items Ordered"}
      className="gap-4 max-md:p-4"
    >
      <div className="overflow-hidden rounded-xl border border-borderDefault">
        {/* Phones: keep the columns readable and scroll the table sideways. */}
        <div className="overflow-x-auto">
        <div className="max-md:min-w-[760px]">
        <div className="grid grid-cols-[64px_1fr_1fr_90px_150px_130px] gap-3 bg-bgLight px-4 py-2.5">
          <div>
            <Text variant="capsBig" tone="dark">Image</Text>
          </div>
          {["Product Name", "SKU", "Qty", "Unit Price", "Total"].map((h, i) => (
            <div key={h} className={i === 4 ? "flex justify-end" : ""}>
              <Text variant="capsBig" tone="dark">{h}</Text>
            </div>
          ))}
        </div>
        {items.map((item) => {
          const lineTotal = item.priceSubtotal ?? item.quantity * item.priceUnit;
          const variantId = item.product?.id ?? null;
          const isNavigating = variantId != null && navigatingId === variantId;

          const rowClasses = "grid grid-cols-[64px_1fr_1fr_90px_150px_130px] items-center gap-3 px-4 py-5";
          const rowContent = (
            <>
              <div className="h-14 w-14 overflow-hidden rounded-lg border border-borderDefault bg-bgLight">
                {item.image ? (
                  <AuthenticatedImage
                    src={item.image}
                    alt={item.name || item.product?.name || "Product"}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Text variant="bodyXs" tone="body">
                      —
                    </Text>
                  </div>
                )}
              </div>
              <Text variant="body" tone="dark" className="min-w-0 truncate">
                {item.name}
              </Text>
              {/* PurchaseOrderLineDto exposes `product` (OdooObjectDto: id+name)
                  but no SKU. BE could embed `defaultCode` so we don't have to
                  show "—" here. */}
              <Text variant="bodySm" tone="muted" className="min-w-0 truncate">
                {item.product?.name ?? "—"}
              </Text>
              <Text variant="body" tone="muted">
                {item.quantity}
              </Text>
              <Text variant="body" tone="muted">
                {item.priceUnit.toLocaleString()}
              </Text>
              <div className="flex justify-end">
                <Text variant="body" tone="muted">
                  {lineTotal.toLocaleString()}
                </Text>
              </div>
            </>
          );

          return variantId != null ? (
            <button
              key={item.id}
              type="button"
              disabled={isNavigating}
              onClick={() => handleRowClick(variantId)}
              className={`${rowClasses} w-full text-left hover:bg-bgLight transition-colors ${isNavigating ? "opacity-60 cursor-wait" : ""}`}
            >
              {rowContent}
            </button>
          ) : (
            <div key={item.id} className={rowClasses}>
              {rowContent}
            </div>
          );
        })}
        </div>
        </div>
      </div>
    </SectionCard>
  );
}
